import type { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { AdminUnpaidInvoiceRow } from "@/admin/entities/adminDashboard";
import {
  UNPAID_STATUS_LABEL,
  formatDay,
  formatUsd,
  organizationTitle,
  overdueLabel,
  unpaidInvoiceHref,
} from "../model";
import { QueryErrorAlert } from "./QueryErrorAlert";

interface UnpaidInvoicesTableProps {
  rows: AdminUnpaidInvoiceRow[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const formatInvoiceAmount = (value: number, currency: string) =>
  `${value.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ${currency}`;

/** Все счета в wait_payment и on_way (спека §3.5), без фильтра по периоду. */
export const UnpaidInvoicesTable: FC<UnpaidInvoicesTableProps> = ({
  rows,
  loading,
  error,
  onRetry,
}) => {
  let body;
  if (error) {
    body = (
      <CardContent>
        <QueryErrorAlert onRetry={onRetry} />
      </CardContent>
    );
  } else if (loading) {
    body = (
      <CardContent>
        <Skeleton variant="rounded" height={160} />
      </CardContent>
    );
  } else if (rows.length === 0) {
    body = (
      <CardContent>
        <Typography color="text.secondary">Все счета оплачены</Typography>
      </CardContent>
    );
  } else {
    body = (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Организация</TableCell>
              <TableCell>Счёт</TableCell>
              <TableCell align="right">Сумма</TableCell>
              <TableCell align="right">В USD</TableCell>
              <TableCell>Дата счёта</TableCell>
              <TableCell>Срок</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {organizationTitle(row.organizationTitle)}
                </TableCell>
                <TableCell>
                  <Link component={RouterLink} to={unpaidInvoiceHref(row)}>
                    {row.invoiceNumber ?? "без номера"}
                  </Link>
                </TableCell>
                <TableCell align="right">
                  {formatInvoiceAmount(row.amountValue, row.amountCurrency)}
                </TableCell>
                <TableCell align="right">
                  {typeof row.amountUSD !== "number" ? (
                    <Tooltip title="Курса на сегодня нет">
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.disabled"
                      >
                        нет курса
                      </Typography>
                    </Tooltip>
                  ) : (
                    formatUsd(row.amountUSD)
                  )}
                </TableCell>
                <TableCell>{formatDay(row.invoiceDate)}</TableCell>
                <TableCell>
                  {row.overdueDays > 0 ? (
                    <Tooltip title={`Срок оплаты ${formatDay(row.expiredAt)}`}>
                      <Chip
                        size="small"
                        color="error"
                        label={overdueLabel(row.overdueDays)}
                      />
                    </Tooltip>
                  ) : (
                    formatDay(row.expiredAt)
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      row.status === "on_way"
                        ? "Клиент сообщил об оплате, деньги в пути"
                        : "Счёт выставлен, ждём оплату"
                    }
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={UNPAID_STATUS_LABEL[row.status] ?? row.status}
                      color={row.status === "on_way" ? "info" : "warning"}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardHeader
        title="Неоплаченные счета"
        subheader="Выставлены и ждут денег, сначала просроченные"
      />
      {body}
    </Card>
  );
};
